import { useState, useEffect, useRef, useCallback } from "react";
import { Sparkles, Send, Plus, Trash2, Loader2, MessageSquare } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { AdminLayout } from "./AdminLayout";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem("admin_token")}` });

const SessionList = ({ sessions, activeId, onSelect, onNew, onDelete }) => (
  <Card className="lg:w-64 shrink-0">
    <CardContent className="p-3 space-y-2">
      <Button className="w-full" onClick={onNew} data-testid="ai-new-chat-btn">
        <Plus className="w-4 h-4 mr-2" /> Chat Baru
      </Button>
      <div className="space-y-1 max-h-[60vh] overflow-y-auto" data-testid="ai-session-list">
        {sessions.map((s) => (
          <div key={s.id} className={`flex items-center gap-1 rounded-md px-2 py-2 text-sm cursor-pointer ${s.id === activeId ? "bg-primary/10" : "hover:bg-muted"}`}>
            <button className="flex-1 flex items-center gap-2 text-left truncate" onClick={() => onSelect(s.id)} data-testid={`ai-session-${s.id}`}>
              <MessageSquare className="w-4 h-4 shrink-0" />
              <span className="truncate">{s.title}</span>
            </button>
            <button onClick={() => onDelete(s.id)} className="text-muted-foreground hover:text-red-500" data-testid={`ai-delete-session-${s.id}`}>
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        ))}
        {sessions.length === 0 && <p className="text-xs text-muted-foreground px-2">Belum ada percakapan.</p>}
      </div>
    </CardContent>
  </Card>
);

const Bubble = ({ m, i }) => (
  <div className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`} data-testid={`ai-msg-${m.role}-${i}`}>
    <div className={`max-w-[85%] rounded-2xl px-4 py-2 text-sm whitespace-pre-wrap ${m.role === "user" ? "bg-primary text-primary-foreground" : "bg-muted"}`}>
      {m.content || <Loader2 className="w-4 h-4 animate-spin" />}
    </div>
  </div>
);

export default function AdminAIAssistant() {
  const [sessions, setSessions] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const endRef = useRef(null);

  const loadSessions = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/admin/ai/sessions`, { headers: authHeader() });
      setSessions(r.data);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { loadSessions(); }, [loadSessions]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const selectSession = async (id) => {
    try {
      const r = await axios.get(`${API}/admin/ai/sessions/${id}`, { headers: authHeader() });
      setActiveId(id);
      setMessages(r.data);
    } catch { toast.error("Gagal memuat percakapan"); }
  };

  const deleteSession = async (id) => {
    if (!window.confirm("Hapus percakapan ini?")) return;
    try {
      await axios.delete(`${API}/admin/ai/sessions/${id}`, { headers: authHeader() });
      if (id === activeId) { setActiveId(null); setMessages([]); }
      loadSessions();
    } catch { toast.error("Gagal menghapus percakapan"); }
  };

  const appendDelta = (delta) => setMessages((prev) => {
    const copy = [...prev];
    copy[copy.length - 1] = { ...copy[copy.length - 1], content: copy[copy.length - 1].content + delta };
    return copy;
  });

  const send = async () => {
    const text = input.trim();
    if (!text || sending) return;
    setInput("");
    setSending(true);
    setMessages((p) => [...p, { role: "user", content: text }, { role: "assistant", content: "" }]);
    try {
      const res = await fetch(`${API}/admin/ai/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeader() },
        body: JSON.stringify({ session_id: activeId, message: text }),
      });
      if (!res.ok) throw new Error((await res.json()).detail || "Gagal mengirim pesan");
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop();
        for (const p of parts) {
          const data = p.replace(/^data: /, "");
          if (data === "[DONE]") continue;
          const ev = JSON.parse(data);
          if (ev.session_id) setActiveId(ev.session_id);
          if (ev.delta) appendDelta(ev.delta);
          if (ev.error) { toast.error(ev.error); appendDelta(ev.error); }
        }
      }
      loadSessions();
    } catch (e) {
      toast.error(e.message);
      setMessages((p) => p.slice(0, -1));
    } finally {
      setSending(false);
    }
  };

  return (
    <AdminLayout title="Asisten AI (ChatGPT)">
      <div className="flex flex-col lg:flex-row gap-4" data-testid="ai-assistant-page">
        <SessionList sessions={sessions} activeId={activeId} onSelect={selectSession}
          onNew={() => { setActiveId(null); setMessages([]); }} onDelete={deleteSession} />
        <Card className="flex-1">
          <CardContent className="p-4 flex flex-col h-[70vh]">
            <div className="flex-1 overflow-y-auto space-y-3 pr-1" data-testid="ai-messages">
              {messages.length === 0 && (
                <div className="h-full flex flex-col items-center justify-center text-muted-foreground text-center gap-2">
                  <Sparkles className="w-10 h-10 text-primary" />
                  <p className="text-sm">Tanyakan apa saja ke Asisten AI — misalnya bantu menyusun pesan tagihan untuk pelanggan.</p>
                </div>
              )}
              {messages.map((m, i) => <Bubble key={i} m={m} i={i} />)}
              <div ref={endRef} />
            </div>
            <div className="flex gap-2 pt-3 border-t mt-3">
              <Textarea rows={2} value={input} onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                placeholder="Ketik pesan..." className="resize-none" data-testid="ai-chat-input" />
              <Button onClick={send} disabled={sending || !input.trim()} data-testid="ai-send-btn">
                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
}
